import { useEffect, useRef, useState } from 'react';
import { T, useGT } from 'gt-react';
import { format } from 'date-fns';
import { parseDateOnly } from '@/utils/dateUtils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import { DecimalInput } from '@/components/common/DecimalInput';
import { CustomersAutocomplete } from '@/components/customer/CustomersAutocomplete';
import {
  useCreateArPayment,
  useUpdateArPayment,
} from '@/api/rpc/hooks/accounts-receivable/useArPayments';
import { useToast } from 'src/context/ToastContext';
import type { ArPaymentDetail, CreateArPaymentInput } from '@erp/rpc';

type PaymentMethod = NonNullable<CreateArPaymentInput['paymentMethod']>;

const PAYMENT_METHOD_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: 'check', label: 'Check' },
  { value: 'wire', label: 'Wire' },
  { value: 'ach', label: 'ACH' },
  { value: 'eft', label: 'EFT' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'other', label: 'Other' },
];

interface PaymentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Provided in edit mode; omit for create. */
  payment?: ArPaymentDetail;
  onSaved?: (paymentId: string) => void;
}

export function PaymentFormDialog({
  open,
  onOpenChange,
  payment,
  onSaved,
}: PaymentFormDialogProps) {
  const gt = useGT();
  const { showToast } = useToast();
  const createMutation = useCreateArPayment();
  const updateMutation = useUpdateArPayment();
  const isEdit = Boolean(payment);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const [customerId, setCustomerId] = useState<string | undefined>(undefined);
  const [payerName, setPayerName] = useState('');
  const [paymentDate, setPaymentDate] = useState<string | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | ''>('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [description, setDescription] = useState('');

  // Re-seed form state only when the dialog opens or targets a different
  // record — keyed on id via a ref so a background refetch (new `payment`
  // object, same id) can't wipe in-progress edits.
  const initedKey = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      initedKey.current = null;
      return;
    }
    const key = payment?.id ?? '__new__';
    if (initedKey.current === key) return;
    initedKey.current = key;
    setCustomerId(payment?.customerId ?? undefined);
    setPayerName(payment?.payerName ?? '');
    setPaymentDate(payment?.paymentDate ?? null);
    setPaymentAmount(payment?.paymentAmount ?? null);
    setPaymentMethod((payment?.paymentMethod as PaymentMethod | null) ?? '');
    setReferenceNumber(payment?.referenceNumber ?? '');
    setDescription(payment?.description ?? '');
  }, [open, payment]);

  const canSubmit =
    paymentAmount !== null && paymentAmount !== '' && !isPending;

  async function handleSubmit() {
    if (!canSubmit) return;
    const fields = {
      customerId: customerId ?? null,
      payerName: payerName.trim() || null,
      paymentDate,
      paymentAmount,
      paymentMethod: paymentMethod || null,
      referenceNumber: referenceNumber.trim() || null,
      description: description.trim() || null,
    };
    try {
      const result =
        isEdit && payment
          ? await updateMutation.mutateAsync({ id: payment.id, ...fields })
          : await createMutation.mutateAsync(fields);
      showToast({
        title: isEdit
          ? gt('Payment updated', { context: 'toast title, payment updated' })
          : gt('Payment created', { context: 'toast title, payment created' }),
        message: result.payment.referenceNumber ?? '',
        color: 'success',
        icon: 'check',
      });
      onSaved?.(result.payment.id);
      onOpenChange(false);
    } catch (err) {
      showToast({
        title: gt('Save failed', {
          context: 'toast title, payment save failed',
        }),
        message:
          err instanceof Error
            ? err.message
            : gt('Failed to save payment', {
                context: 'toast message, payment save failed',
              }),
        color: 'error',
        icon: 'warning',
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? (
              <T context="dialog title, edit a payment">Edit payment</T>
            ) : (
              <T context="dialog title, record a new payment">
                Record a payment
              </T>
            )}
          </DialogTitle>
          <DialogDescription>
            <T context="dialog description, manual payment entry">
              Manually record a payment. Apply it to invoices from the payment
              detail after saving.
            </T>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>
              <T context="form label, payment customer">Customer (optional)</T>
            </Label>
            <CustomersAutocomplete
              selectedCustomerId={customerId}
              onCustomerSelect={setCustomerId}
              allowClear
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="pay-amount">
                <T context="form label, payment amount">Amount</T>
              </Label>
              <DecimalInput
                id="pay-amount"
                value={paymentAmount}
                onChange={setPaymentAmount}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pay-date">
                <T context="form label, payment date">Date</T>
              </Label>
              <DatePicker
                value={
                  paymentDate
                    ? (parseDateOnly(paymentDate) ?? undefined)
                    : undefined
                }
                onChange={(d) =>
                  setPaymentDate(d ? format(d, 'yyyy-MM-dd') : null)
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="pay-method">
                <T context="form label, payment method">Method</T>
              </Label>
              <Select
                value={paymentMethod}
                onValueChange={(v) => setPaymentMethod(v as PaymentMethod)}
              >
                <SelectTrigger id="pay-method">
                  <SelectValue
                    placeholder={gt('Method', {
                      context: 'placeholder, payment method',
                    })}
                  />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHOD_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pay-ref">
                <T context="form label, payment reference number">
                  Reference #
                </T>
              </Label>
              <Input
                id="pay-ref"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                placeholder={gt('Check / wire #', {
                  context: 'placeholder, payment reference',
                })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="pay-desc">
              <T context="form label, payment description">
                Description (optional)
              </T>
            </Label>
            <Textarea
              id="pay-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            <T context="button label, cancel dialog">Cancel</T>
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isEdit ? (
              <T context="button label, save payment edits">Save</T>
            ) : (
              <T context="button label, create payment">Create</T>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
